/**
 * SchedulingLinkForm
 *
 * Form for creating or editing a scheduling link.
 * Includes title, slug, duration, location, color, calendar, timezone,
 * availability windows, buffer times, and daily cap.
 */

import { useState, useEffect } from 'react';
import { DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type { SchedulingLinkDetail, SchedulingLinkInput, AvailabilityWindow } from '@/lib/api';
import type { CalendarCollection } from '../types';
import { PRESET_COLORS, DAY_NAMES } from '../types';
import { ColorPicker } from './ColorPicker';

interface SchedulingLinkFormProps {
  initialData: SchedulingLinkDetail | null;
  calendars: CalendarCollection[];
  onSave: (input: SchedulingLinkInput) => Promise<void>;
  onBack: () => void;
  defaultAvailabilityWindows?: { day: number; startHour: number; endHour: number }[];
  defaultBookingConfirmationMessage?: string;
}

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function settingsToWindows(
  settings?: { day: number; startHour: number; endHour: number }[],
): AvailabilityWindow[] {
  if (!settings || settings.length === 0) {
    return [
      { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' },
      { dayOfWeek: 2, startTime: '09:00', endTime: '17:00' },
      { dayOfWeek: 3, startTime: '09:00', endTime: '17:00' },
      { dayOfWeek: 4, startTime: '09:00', endTime: '17:00' },
      { dayOfWeek: 5, startTime: '09:00', endTime: '17:00' },
    ];
  }
  return settings.map((w) => ({
    dayOfWeek: w.day,
    startTime: `${String(w.startHour).padStart(2, '0')}:00`,
    endTime: `${String(w.endHour).padStart(2, '0')}:00`,
  }));
}

export function SchedulingLinkForm({
  initialData,
  calendars,
  onSave,
  onBack,
  defaultAvailabilityWindows,
  defaultBookingConfirmationMessage,
}: SchedulingLinkFormProps) {
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState(
    !initialData && defaultBookingConfirmationMessage ? defaultBookingConfirmationMessage : '',
  );
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [location, setLocation] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [showColor, setShowColor] = useState(false);
  const [calendarId, setCalendarId] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [bufferBeforeMinutes, setBufferBeforeMinutes] = useState(0);
  const [bufferAfterMinutes, setBufferAfterMinutes] = useState(0);
  const [dailyCap, setDailyCap] = useState<number | null>(null);
  const [windows, setWindows] = useState<AvailabilityWindow[]>(
    !initialData
      ? settingsToWindows(defaultAvailabilityWindows)
      : [
          { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' },
          { dayOfWeek: 2, startTime: '09:00', endTime: '17:00' },
          { dayOfWeek: 3, startTime: '09:00', endTime: '17:00' },
          { dayOfWeek: 4, startTime: '09:00', endTime: '17:00' },
          { dayOfWeek: 5, startTime: '09:00', endTime: '17:00' },
        ],
  );

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setSlug(initialData.slug);
      setSlugManuallyEdited(true);
      setDescription(initialData.description || '');
      setDurationMinutes(initialData.durationMinutes);
      setLocation(initialData.location || '');
      setColor(initialData.color);
      setShowColor(!!initialData.color);
      setCalendarId(initialData.calendarId || '');
      setTimezone(initialData.timezone);
      setBufferBeforeMinutes(initialData.bufferBeforeMinutes);
      setBufferAfterMinutes(initialData.bufferAfterMinutes);
      setDailyCap(initialData.dailyCap);
      setWindows(
        initialData.availabilityWindows.length > 0
          ? initialData.availabilityWindows
          : [{ dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }],
      );
    }
  }, [initialData]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    setSlugManuallyEdited(true);
  };

  const toggleDay = (day: number) => {
    const existing = windows.find((w) => w.dayOfWeek === day);
    if (existing) {
      setWindows((prev) => prev.filter((w) => w.dayOfWeek !== day));
    } else {
      setWindows((prev) =>
        [...prev, { dayOfWeek: day, startTime: '09:00', endTime: '17:00' }].sort(
          (a, b) => a.dayOfWeek - b.dayOfWeek,
        ),
      );
    }
  };

  const updateWindowTime = (day: number, field: 'startTime' | 'endTime', value: string) => {
    setWindows((prev) => prev.map((w) => (w.dayOfWeek === day ? { ...w, [field]: value } : w)));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    if (!slug.trim() || slug.length < 2) {
      toast({ title: 'A valid slug is required (at least 2 characters)', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await onSave({
        slug: slug.trim(),
        title: title.trim(),
        description: description || undefined,
        durationMinutes,
        location: location || undefined,
        color: showColor ? color || undefined : undefined,
        calendarId: calendarId || undefined,
        timezone,
        bufferBeforeMinutes,
        bufferAfterMinutes,
        dailyCap,
        availabilityWindows: windows,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-3 py-1">
        <div>
          <Label className="text-xs">Title</Label>
          <Input
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="e.g., 30 Minute Meeting"
            className="text-sm"
            autoFocus
          />
        </div>

        <div>
          <Label className="text-xs">URL Slug</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">/book/</span>
            <Input
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="my-meeting"
              className="text-sm flex-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description for your booking page"
            rows={2}
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Duration</Label>
            <Select
              value={String(durationMinutes)}
              onValueChange={(v) => setDurationMinutes(parseInt(v, 10))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_PRESETS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d} minutes
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Timezone</Label>
            <Input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="text-sm"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Location</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Zoom, Google Meet, Office, etc."
            className="text-sm"
          />
        </div>

        {calendars.length > 0 && (
          <div>
            <Label className="text-xs">Calendar</Label>
            <Select value={calendarId} onValueChange={setCalendarId}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Default calendar" />
              </SelectTrigger>
              <SelectContent>
                {calendars.map((cal) => (
                  <SelectItem key={cal.id} value={cal.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: cal.color }}
                      />
                      {cal.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Availability Windows */}
        <div>
          <Label className="text-xs">Availability</Label>
          <div className="space-y-2 mt-1">
            <div className="flex flex-wrap gap-1.5">
              {DAY_NAMES.map((name, i) => {
                const isActive = windows.some((w) => w.dayOfWeek === i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`text-xs px-2 py-1 rounded border ${
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-muted-foreground'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            {windows
              .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
              .map((w) => (
                <div key={w.dayOfWeek} className="flex items-center gap-2 text-xs">
                  <span className="w-8">{DAY_NAMES[w.dayOfWeek]}</span>
                  <Input
                    type="time"
                    value={w.startTime}
                    onChange={(e) => updateWindowTime(w.dayOfWeek, 'startTime', e.target.value)}
                    className="text-xs h-7 w-28"
                  />
                  <span>-</span>
                  <Input
                    type="time"
                    value={w.endTime}
                    onChange={(e) => updateWindowTime(w.dayOfWeek, 'endTime', e.target.value)}
                    className="text-xs h-7 w-28"
                  />
                </div>
              ))}
          </div>
        </div>

        {/* Buffers */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Buffer before (min)</Label>
            <Input
              type="number"
              min={0}
              max={120}
              value={bufferBeforeMinutes}
              onChange={(e) => setBufferBeforeMinutes(parseInt(e.target.value, 10) || 0)}
              className="text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Buffer after (min)</Label>
            <Input
              type="number"
              min={0}
              max={120}
              value={bufferAfterMinutes}
              onChange={(e) => setBufferAfterMinutes(parseInt(e.target.value, 10) || 0)}
              className="text-sm"
            />
          </div>
        </div>

        {/* Daily cap */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={dailyCap !== null}
              onCheckedChange={(c) => setDailyCap(c ? 5 : null)}
            />
            <span className="text-xs">Limit daily bookings</span>
          </label>
          {dailyCap !== null && (
            <Input
              type="number"
              min={1}
              value={dailyCap}
              onChange={(e) => setDailyCap(parseInt(e.target.value, 10) || 1)}
              className="text-sm mt-1"
            />
          )}
        </div>

        {/* Color */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <Checkbox
              checked={showColor}
              onCheckedChange={(c) => {
                setShowColor(c === true);
                if (!c) setColor(null);
              }}
            />
            <span className="text-xs">Custom color</span>
          </label>
          {showColor && <ColorPicker value={color || PRESET_COLORS[0]} onChange={setColor} />}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={saving}>
          Back
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {initialData ? 'Save Changes' : 'Create Link'}
        </Button>
      </DialogFooter>
    </>
  );
}
